import { Component, Inject } from '@angular/core';
import { FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  ActivityBodyInterface,
  ActivityInterface,
  ActivityTypeEnum,
} from '@sport-activities/sdk';

import { appConfig } from '~/app.config';
import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { noHtmlTagValidator } from '~/core/validators/no-html-tag/no-html-tag.validator';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { OrganizerSharedPageContainerTipConfigInterface } from '~/organizer/shared/components/shared/page-container/interfaces/tip.config.interface';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedLocatableService } from '~/organizer/shared/services/locatable/locatable.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';

@Component({
  selector: 'app-organizer-activity-creation-description',
  templateUrl: './description.component.html',
  styleUrls: [
    '../../../../../organizer/shared/components/shared/form-wrapper/form-wrapper.component.scss',
  ],
})
export class OrganizerActivityCreationPageDescriptionComponent extends AbstractOrganizerActivityCreationFormPageDirective {
  protected readonly pageTitleKey = `seo.organizer.${this.translateKey(
    'title'
  )}`;

  public readonly minLength =
    appConfig.formValidationRules.activityDescriptionMinimumLength;
  public readonly tipsConfig: OrganizerSharedPageContainerTipConfigInterface[] =
    [
      {
        descriptionKey: this.translateKey('tips.description'),
        headerKey: this.translateKey('tips.header'),
      },
    ];

  constructor(
    private readonly locatableService: OrganizerSharedLocatableService,
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService,
    private readonly translateService: TranslateService
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  public get descriptionErrors(): ValidationErrors {
    const description = this.formGroup.get('description');

    return description?.touched && description?.errors;
  }

  protected buildActivityData(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityInterface> {
    return {
      description: formGroupValue.description,
    };
  }

  protected buildDataBeforeSubmit(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityBodyInterface> {
    return {
      description: [
        this.locatableService.buildCurrentLocatableBody(
          formGroupValue.description
        ),
      ],
    };
  }

  protected buildValidationConfig(): void {
    const activity = this.activityService.activity;

    this.validationConfig = {
      description: [
        activity.description || '',
        [
          Validators.required,
          Validators.minLength(this.minLength),
          noHtmlTagValidator(),
        ],
      ],
    };
  }

  public submit(formGroupValue: FormGroupValueInterface): void {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    super.submit(formGroupValue);
  }

  public translateKey(key: string, interpolateParams?: object): string {
    let baseKey = 'activity_creation.description.';
    if (
      this.activityService.activity.type === ('BIRTHDAY' as ActivityTypeEnum) ||
      this.activityService?.activity?.type ===
        ('PRODUCT_TEST' as ActivityTypeEnum)
    ) {
      baseKey = `${baseKey}${this.activityService?.activity?.type?.toLowerCase()}.`;
    }

    return this.translateService.instant(`${baseKey}${key}`, interpolateParams);
  }
}
